import { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from 'react-bootstrap';

import Label from './form/Label';

const GenericButtonGroup = ({
    heading,
    labelComponent,
    tooltip,
    options = [],
    valueKey = 'value',
    displayValueKey = 'displayValue',
    defaultValue,
    placeholder = 'OTM_BUTTON_GROUP_CLEAR',
    addEmptyOption = false,
    allowMultiple = false,
    onSelect = () => { },
    readOnly = false,
    disabled = false,
    buttonTooltip = false,
    isRequired = false,
    variant = 'outline-primary',
    activeVariant = 'primary',
    id,
    name,
}) => {
    const [selectedValues, setSelectedValues] = useState([]);

    useEffect(() => {
        setSelectedValues(Array.isArray(defaultValue) ? [...defaultValue] : (defaultValue ? [defaultValue] : []));
    }, [defaultValue]);

    const allOptions = useMemo(() => {
        const result = options.map((option) => option);

        if (addEmptyOption) {
            result.unshift({
                [valueKey]: '',
                [displayValueKey]: placeholder,
            });
        }

        return result;
    }, [addEmptyOption, valueKey, displayValueKey, placeholder, options]);

    const getOptionDisplay = useCallback((value) => {
        const option = allOptions.find((option) => option[valueKey] === value);

        return (option && option[displayValueKey]) || value;
    }, [allOptions, valueKey, displayValueKey]);

    const handleChange = useCallback((eventKey) => {
        setSelectedValues((currentValues) => {
            let result = [];
            if (currentValues.includes(eventKey)) {
                result = allowMultiple ? currentValues.filter((selectedValue) => selectedValue !== eventKey) : [];
            }
            else {
                result = allowMultiple ? [...currentValues, eventKey] : [eventKey];
            }

            onSelect(allowMultiple ? result : result.at(0));

            return result;
        });
    }, [allowMultiple, onSelect]);

    return (
        <>
            <Label
                controlId={id || name}
                label={heading}
                labelComponent={labelComponent}
                tooltip={tooltip}
                buttonTooltip={buttonTooltip}
                isRequired={isRequired}
            />
            <div className='row mt-n1 ms-n1'>
                {allOptions.map((option, index) => {
                    const value = option[valueKey];
                    const displayValue = getOptionDisplay(value);

                    return (
                        <div
                            key={index}
                            className={`col p-0 mt-1 ms-1`}
                        >
                            <Button
                                variant={selectedValues.includes(value) ? (option.activeVariant || activeVariant) : (option.variant || variant)}
                                readOnly={readOnly}
                                disabled={disabled}
                                onClick={() => {
                                    handleChange(value);
                                }}
                                className='h-100 w-100'
                            >
                                {`${displayValue}`}
                            </Button>
                        </div>
                    );
                })}
            </div>
        </>
    );
};

export default GenericButtonGroup;